/**
 * Module #13 — Consignment Terms admin. Tenant-scoped consignment profiles
 * (commission rate + fee schedule) applied at auction setup; the settlement
 * pipeline resolves the rate per lot. Mirrors the AdminTaxonomies CRUD pattern.
 */

import { Badge, Button, HelpTooltip } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import type { Json, Tables } from '@auctisync/shared';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';
import {
  ProfileAdminPage,
  Panel,
  InlineError,
  SkeletonList,
  DollarInput,
  PercentInput,
  listStyle,
  inputStyle,
  selectStyle,
  editorBoxStyle,
  readRowStyle,
} from './profileKit';
import {
  FEE_TYPES,
  APPLIES_TO,
  APPLIES_TO_LABEL,
  bpsToPct,
  feeToRow,
  newFee,
  validateConsignment,
  profileSaveError,
  type FeeDraft,
} from './profileForms';

type Profile = Tables<'consignment_profiles'>;
type Fee = Tables<'consignment_profile_fees'>;

export function AdminConsignmentProfiles() {
  return (
    <ProfileAdminPage
      title="Consignment Terms"
      subtitle="Reusable seller terms — commission rate + fee schedule — applied to an auction, consignor, or lot."
      helpSlug="consignment-profiles"
      learnMore="About consignment terms"
      emptyDescription="Consignment terms are tenant-scoped. Pick a tenant above to manage their profiles."
    >
      {(tenantId) => <ConsignmentProfilesPanel tenantId={tenantId} />}
    </ProfileAdminPage>
  );
}

function ConsignmentProfilesPanel({ tenantId }: { tenantId: string }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [feesByProfile, setFeesByProfile] = useState<Record<string, Fee[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function refresh() {
    const [{ data: profs, error: pErr }, { data: fees, error: fErr }] = await Promise.all([
      supabase
        .from('consignment_profiles')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('is_default', { ascending: false })
        .order('name', { ascending: true }),
      supabase
        .from('consignment_profile_fees')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('sort_order', { ascending: true }),
    ]);
    if (pErr || fErr) {
      setError((pErr ?? fErr)?.message ?? 'Failed to load.');
      return;
    }
    setProfiles(profs ?? []);
    const grouped: Record<string, Fee[]> = {};
    for (const f of fees ?? []) (grouped[f.profile_id] ??= []).push(f);
    setFeesByProfile(grouped);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleArchive(id: string) {
    if (
      !window.confirm('Archive this profile? Auctions/consignors/lots keep their settled history.')
    )
      return;
    const { error: err } = await supabase
      .from('consignment_profiles')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setProfiles((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <Panel
      title="Consignment profiles"
      subtitle="Each profile is a commission rate plus an optional fee schedule."
      helpSlug="consignment-profiles"
      onAddClick={() => setEditingId('new')}
    >
      {error && <InlineError>{error}</InlineError>}
      {editingId === 'new' && (
        <ConsignmentProfileEditor
          tenantId={tenantId}
          profile={null}
          fees={[]}
          existingNames={profiles.map((p) => p.name)}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            await refresh();
            setEditingId(null);
          }}
        />
      )}
      {loading ? (
        <SkeletonList />
      ) : profiles.length === 0 && editingId === null ? (
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No consignment profiles yet. Add one (mark it the default) so auctions can settle.
        </span>
      ) : (
        <ul style={{ ...listStyle, gap: '0.5rem' }}>
          {profiles.map((p) =>
            editingId === p.id ? (
              <li key={p.id}>
                <ConsignmentProfileEditor
                  tenantId={tenantId}
                  profile={p}
                  fees={feesByProfile[p.id] ?? []}
                  existingNames={profiles.filter((x) => x.id !== p.id).map((x) => x.name)}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    await refresh();
                    setEditingId(null);
                  }}
                />
              </li>
            ) : (
              <li key={p.id}>
                <ProfileRow
                  profile={p}
                  feeCount={(feesByProfile[p.id] ?? []).length}
                  onEdit={() => setEditingId(p.id)}
                  onArchive={() => void handleArchive(p.id)}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </Panel>
  );
}

function ProfileRow({
  profile,
  feeCount,
  onEdit,
  onArchive,
}: {
  profile: Profile;
  feeCount: number;
  onEdit: () => void;
  onArchive: () => void;
}) {
  return (
    <div style={readRowStyle}>
      <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--text)' }}>{profile.name}</strong>
          {profile.is_default && <Badge variant="success">Default</Badge>}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
          {bpsToPct(profile.commission_rate_bps)}% commission · {feeCount} fee
          {feeCount === 1 ? '' : 's'}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={onArchive}>
          Archive
        </Button>
      </div>
    </div>
  );
}

function feeToDraft(f: Fee): FeeDraft {
  const percentMode = f.amount_bps !== null;
  return {
    fee_type: f.fee_type as FeeDraft['fee_type'],
    label: f.label,
    mode: percentMode ? 'percent' : 'flat',
    percent: f.amount_bps !== null ? bpsToPct(f.amount_bps) : '',
    dollars: f.amount_cents !== null ? (f.amount_cents / 100).toFixed(2) : '',
    applies_to: f.applies_to as FeeDraft['applies_to'],
  };
}

function ConsignmentProfileEditor({
  tenantId,
  profile,
  fees,
  existingNames,
  onCancel,
  onSaved,
}: {
  tenantId: string;
  profile: Profile | null;
  fees: Fee[];
  existingNames: string[];
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [commissionPct, setCommissionPct] = useState(
    profile ? bpsToPct(profile.commission_rate_bps) : '25.00',
  );
  const [isDefault, setIsDefault] = useState(profile?.is_default ?? false);
  const [feeDrafts, setFeeDrafts] = useState<FeeDraft[]>(() => fees.map(feeToDraft));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  function setFee(idx: number, patch: Partial<FeeDraft>) {
    setFeeDrafts((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  }

  async function handleSave() {
    if (saving) return; // re-entrancy guard (beyond the disabled button)
    const errs = validateConsignment(name, commissionPct, feeDrafts, existingNames);
    if (errs.length > 0) {
      setError(errs[0] ?? 'Please fix the highlighted fields.');
      return;
    }
    setError(null);
    setSaving(true);

    // One atomic RPC: demote prior default (if default) + upsert + replace fees.
    // A failure rolls the whole save back, so the tenant is never stranded with
    // zero defaults or wiped fees (the no-transaction hole the review found).
    // gen-types over-narrows nullable uuid/text args to `string` and jsonb to
    // `Json`; the runtime values are valid for the SQL (the fn handles null).
    const { data, error: err } = await supabase.rpc('save_consignment_profile', {
      p_id: (profile?.id ?? null) as unknown as string,
      p_tenant_id: tenantId,
      p_name: name.trim(),
      p_description: (description.trim() === '' ? null : description.trim()) as unknown as string,
      p_commission_bps: Math.round(Number(commissionPct) * 100),
      p_is_default: isDefault,
      // Raw array — supabase-js serializes to jsonb (never JSON.stringify a jsonb param).
      p_fees: feeDrafts.map((f, i) => feeToRow(f, i)) as unknown as Json,
    });
    setSaving(false);
    if (err || !data) {
      setError(err ? profileSaveError(err) : 'Could not save the profile.');
      return;
    }
    toast.success('Consignment profile saved.');
    await onSaved();
  }

  return (
    <div style={editorBoxStyle}>
      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '2fr 1fr auto' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Profile name (e.g., Standard 25%)"
          aria-label="Profile name"
          style={inputStyle}
        />
        <label style={pctFieldStyle}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Commission</span>
          <PercentInput
            value={commissionPct}
            onChange={setCommissionPct}
            ariaLabel="Commission percent"
          />
        </label>
        <label style={defaultLabelStyle}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Default
          <HelpTooltip helpSlug="consignment-profiles">
            Only one consignment profile per tenant can be the default.
          </HelpTooltip>
        </label>
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      <div>
        <div style={subheadStyle}>Fee schedule</div>
        {feeDrafts.length === 0 ? (
          <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: '0 0 0.4rem' }}>
            No fees — commission only.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '0.4rem' }}>
            {feeDrafts.map((fee, idx) => (
              <div key={idx} style={feeRowStyle}>
                <select
                  value={fee.fee_type}
                  onChange={(e) =>
                    setFee(idx, { fee_type: e.target.value as FeeDraft['fee_type'] })
                  }
                  aria-label="Fee type"
                  style={selectStyle}
                >
                  {FEE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={fee.label}
                  onChange={(e) => setFee(idx, { label: e.target.value })}
                  placeholder="Label (e.g., Lot fee)"
                  aria-label="Fee label"
                  style={inputStyle}
                />
                <select
                  value={fee.mode}
                  onChange={(e) => setFee(idx, { mode: e.target.value as FeeDraft['mode'] })}
                  aria-label="Fee amount type"
                  style={selectStyle}
                >
                  <option value="flat">Flat $</option>
                  <option value="percent">Percent %</option>
                </select>
                {fee.mode === 'flat' ? (
                  <DollarInput value={fee.dollars} onChange={(v) => setFee(idx, { dollars: v })} />
                ) : (
                  <PercentInput
                    value={fee.percent}
                    onChange={(v) => setFee(idx, { percent: v })}
                    ariaLabel="Fee percent"
                  />
                )}
                <select
                  value={fee.applies_to}
                  onChange={(e) =>
                    setFee(idx, { applies_to: e.target.value as FeeDraft['applies_to'] })
                  }
                  aria-label="Applies to"
                  style={selectStyle}
                >
                  {APPLIES_TO.map((a) => (
                    <option key={a} value={a}>
                      {APPLIES_TO_LABEL[a]}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setFeeDrafts((prev) => prev.filter((_, i) => i !== idx))}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: '0.5rem' }}>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setFeeDrafts((prev) => [...prev, newFee()])}
          >
            + Add fee
          </Button>
        </div>
      </div>

      {error && <InlineError>{error}</InlineError>}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={saving} onClick={() => void handleSave()}>
          {saving ? 'Saving…' : profile ? 'Save changes' : 'Create profile'}
        </Button>
      </div>
    </div>
  );
}

const subheadStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: '0.35rem',
};
const feeRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns:
    'minmax(7rem,1fr) minmax(9rem,1.4fr) minmax(6rem,auto) minmax(6rem,1fr) minmax(8rem,auto) auto',
  gap: '0.4rem',
  alignItems: 'center',
};
const pctFieldStyle: React.CSSProperties = { display: 'grid', gap: '0.2rem', alignItems: 'center' };
const defaultLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  fontSize: '0.85rem',
  color: 'var(--text)',
  alignSelf: 'end',
};
